// Renderer for scheme background image (blueprint/plan)

import * as THREE from 'three'
import type { GardenScene } from './GardenScene'
import { debug } from '@/utils/debug'
import { DPM_SCALE } from '@/constants/coordinates'
import type { SchemeImage, SchemeOutline } from '@/types'
import type { GeoAnchor } from '@/geodata/types'

/** Default tube radius for boundary outline */
const TUBE_RADIUS_NORMAL = 0.025
/** Thicker tube radius when map tab is active (easier to see and click) */
const TUBE_RADIUS_THICK = 0.06

export class SchemeRenderer {
  private scene: GardenScene
  private mesh: THREE.Mesh | null = null
  private outlineMesh: THREE.Mesh | null = null
  private anchorMarker: THREE.Group | null = null
  private textureLoader: THREE.TextureLoader
  private visible: boolean = true
  private currentOutline: SchemeOutline | null = null
  private outlineThick: boolean = false
  private anchorMarkerVisible: boolean = false

  constructor(scene: GardenScene) {
    this.scene = scene
    this.textureLoader = new THREE.TextureLoader()
  }

  /**
   * Load and render the scheme background image
   */
  async load(schemeImage: SchemeImage): Promise<void> {
    // Check if we have an image URL
    if (!schemeImage.imageUrl) {
      debug.log('Scheme', 'No scheme image URL provided, skipping')
      return
    }

    debug.log('Scheme', `Loading scheme image: ${schemeImage.imageUrl}`)
    debug.log('Scheme', `Position: (${schemeImage.x}, ${schemeImage.y}), Scale: ${schemeImage.scale}`)

    try {
      // Load the texture
      const texture = await this.loadTexture(schemeImage.imageUrl)

      // Get image dimensions from texture
      const imageWidth = texture.image.width
      const imageHeight = texture.image.height
      debug.log('Scheme', `Image dimensions: ${imageWidth}x${imageHeight}px`)

      // Apply scale factor (fallback to 1 if invalid)
      const scale = schemeImage.scale > 0 ? schemeImage.scale : 1

      // Calculate display size in points, then convert to world units
      // The scale is applied to the image size
      const displayWidthPoints = imageWidth * scale
      const displayHeightPoints = imageHeight * scale

      const displayWidth = displayWidthPoints * DPM_SCALE
      const displayHeight = displayHeightPoints * DPM_SCALE

      debug.log('Scheme', `Display size: ${displayWidthPoints}x${displayHeightPoints} points (${displayWidth.toFixed(2)}x${displayHeight.toFixed(2)}m)`)

      // Create geometry
      const geometry = new THREE.PlaneGeometry(displayWidth, displayHeight)

      // Create material with the texture
      // Use MeshBasicMaterial since this is a background image (no lighting needed)
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: false, // Prevent z-fighting with ground shapes
        side: THREE.DoubleSide
      })

      // Remove existing mesh if any
      this.dispose()

      // Create mesh
      this.mesh = new THREE.Mesh(geometry, material)

      // Position the mesh
      // x, y are top-left corner in points
      // Three.js plane is centered, so we need to offset by half the display size
      let posX = (schemeImage.x + displayWidthPoints / 2) * DPM_SCALE
      let posZ = (schemeImage.y + displayHeightPoints / 2) * DPM_SCALE

      // Place slightly below ground level to ensure it's behind ground shapes
      const posY = -0.02

      // Rotate to be horizontal (XZ plane)
      this.mesh.rotation.x = -Math.PI / 2

      // Apply rotation around the geo-anchor (editor origin 0,0) if set.
      // The WMS image is north-up; the boundary was rotated by garden.rotation degrees
      // using rotateBoundaryPoints (2D: x'=x·cos-y·sin, y'=x·sin+y·cos).
      // After rotation.x=-PI/2, rotation.z rotates the mesh content in the opposite
      // sense to the editor 2D rotation, so we negate it.
      const rotationRad = schemeImage.rotation ? (schemeImage.rotation * Math.PI / 180) : 0
      if (rotationRad !== 0) {
        // Negate: rotation.z is opposite-handed to editor 2D rotation after the X flip
        this.mesh.rotation.z = -rotationRad

        // Rotate position in XZ plane to match editor 2D rotation convention
        const cosR = Math.cos(rotationRad)
        const sinR = Math.sin(rotationRad)
        const newPosX = posX * cosR - posZ * sinR
        const newPosZ = posX * sinR + posZ * cosR
        posX = newPosX
        posZ = newPosZ
      }

      this.mesh.position.set(posX, posY, posZ)

      // Set render order to ensure it renders before ground shapes
      this.mesh.renderOrder = -1

      // Apply visibility
      this.mesh.visible = this.visible

      // Add to scene
      this.scene.getScene().add(this.mesh)

      debug.success('Scheme', `Scheme image rendered at (${posX.toFixed(2)}, ${posY}, ${posZ.toFixed(2)})`)

    } catch (error) {
      debug.error('Scheme', 'Failed to load scheme image', error)
    }
  }

  /**
   * Load texture with promise wrapper
   */
  private loadTexture(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace
          resolve(texture)
        },
        undefined,
        (error) => {
          reject(error)
        }
      )
    })
  }

  /**
   * Render the garden boundary outline as 3D tubes with sharp corners
   */
  renderOutline(outline: SchemeOutline): void {
    // Store current outline for re-rendering with different thickness
    this.currentOutline = (outline.points && outline.points.length >= 3) ? outline : null

    // Clean up existing outline mesh first (before guard check)
    if (this.outlineMesh) {
      this.scene.getScene().remove(this.outlineMesh)
      // Handle both single mesh and group
      if ((this.outlineMesh as unknown as THREE.Group).isGroup) {
        const group = this.outlineMesh as unknown as THREE.Group
        group.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose()
            if (child.material instanceof THREE.Material) {
              child.material.dispose()
            }
          }
        })
      } else {
        this.outlineMesh.geometry?.dispose()
        if (Array.isArray(this.outlineMesh.material)) {
          this.outlineMesh.material.forEach(m => m.dispose())
        } else if (this.outlineMesh.material instanceof THREE.Material) {
          this.outlineMesh.material.dispose()
        }
      }
      this.outlineMesh = null
      debug.log('Scheme', 'Cleared existing outline')
    }

    // Guard: need at least 3 points for a valid polygon
    if (!outline.points || outline.points.length < 3) {
      debug.log('Scheme', 'No outline to render (insufficient points)')
      return
    }

    const TUBE_RADIUS = this.outlineThick ? TUBE_RADIUS_THICK : TUBE_RADIUS_NORMAL
    const TUBE_SEGMENTS = 8
    const Y_HEIGHT = 0.025

    // Convert 2D points to 3D
    const points3D = outline.points.map(p =>
      new THREE.Vector3(
        (outline.x + p.x) * DPM_SCALE,
        Y_HEIGHT,
        (outline.y + p.y) * DPM_SCALE
      )
    )

    // Create material (shared by all segments)
    const material = new THREE.MeshStandardMaterial({ color: 0xff0000 })

    // Create a group to hold all segments
    const group = new THREE.Group()

    // Create tube segments for each edge (with sharp corners)
    for (let i = 0; i < points3D.length; i++) {
      const start = points3D[i]
      const end = points3D[(i + 1) % points3D.length]

      // Create a straight line path for this segment
      const linePath = new THREE.LineCurve3(start, end)
      const tubeGeometry = new THREE.TubeGeometry(linePath, 1, TUBE_RADIUS, TUBE_SEGMENTS, false)

      const tubeMesh = new THREE.Mesh(tubeGeometry, material)
      tubeMesh.castShadow = false  // Disable shadow casting to avoid dotted shadow effect
      tubeMesh.receiveShadow = false
      group.add(tubeMesh)

      // Add sphere at each corner for clean joints
      const sphereGeometry = new THREE.SphereGeometry(TUBE_RADIUS, TUBE_SEGMENTS, TUBE_SEGMENTS)
      const sphereMesh = new THREE.Mesh(sphereGeometry, material)
      sphereMesh.position.copy(start)
      sphereMesh.castShadow = false
      sphereMesh.receiveShadow = false
      group.add(sphereMesh)
    }

    // Semi-transparent fill polygon
    // ShapeGeometry lives in local XY; rotation.x = -PI/2 maps local Y → world -Z,
    // so we negate Z here so it ends up at the correct world Z position.
    const fillShape = new THREE.Shape()
    fillShape.moveTo(points3D[0].x, -points3D[0].z)
    for (let i = 1; i < points3D.length; i++) {
      fillShape.lineTo(points3D[i].x, -points3D[i].z)
    }
    fillShape.closePath()

    const fillGeometry = new THREE.ShapeGeometry(fillShape)
    const fillMaterial = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.08,
      side: THREE.DoubleSide,
      depthWrite: false,
    })
    const fillMesh = new THREE.Mesh(fillGeometry, fillMaterial)
    fillMesh.rotation.x = -Math.PI / 2 // Lay flat on XZ plane
    fillMesh.position.y = Y_HEIGHT - 0.01
    fillMesh.castShadow = false
    fillMesh.receiveShadow = false
    group.add(fillMesh)

    // Tag group for selection manager (T07a)
    group.name = 'boundary_outline'
    group.userData.boundaryData = {
      id: 'boundary',
      type: 'boundary',
      x: outline.x,
      y: outline.y,
      points: outline.points,
    }

    // Store the group as the outline mesh
    group.visible = this.visible
    this.outlineMesh = group as unknown as THREE.Mesh
    this.scene.getScene().add(group)

    debug.success('Scheme', `Outline rendered with ${outline.points.length} points (sharp corners)`)
  }

  /**
   * Get the outline group (for SelectionManager)
   */
  getOutlineGroup(): THREE.Group | null {
    if (!this.outlineMesh) return null
    return this.outlineMesh as unknown as THREE.Group
  }

  /**
   * Set boundary outline thickness mode.
   * When thick=true (map tab active), uses a larger tube radius for easier clicking.
   * Returns true if the outline was re-rendered.
   */
  setOutlineThick(thick: boolean): boolean {
    if (this.outlineThick === thick) return false
    this.outlineThick = thick
    if (this.currentOutline) {
      this.renderOutline(this.currentOutline)
      return true
    }
    return false
  }

  /**
   * Render a pin marker at the geo-anchor position showing lat/lon.
   * Only call when a geo-anchor is set. Pass null to remove.
   */
  renderGeoAnchor(anchor: GeoAnchor | null): void {
    // Clean up existing marker
    if (this.anchorMarker) {
      this.scene.getScene().remove(this.anchorMarker)
      this.anchorMarker.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          if (child.material instanceof THREE.Material) child.material.dispose()
        }
      })
      this.anchorMarker = null
    }

    if (!anchor) return

    const group = new THREE.Group()
    group.name = 'geo_anchor_marker'

    const ex = anchor.editorOrigin.x * DPM_SCALE
    const ez = anchor.editorOrigin.y * DPM_SCALE
    const color = 0x3b82f6 // Blue

    // Pin body: inverted cone (point down)
    const coneH = 0.5
    const coneR = 0.15
    const coneGeo = new THREE.ConeGeometry(coneR, coneH, 12)
    const coneMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 })
    const cone = new THREE.Mesh(coneGeo, coneMat)
    cone.rotation.x = Math.PI // Flip so point faces down
    cone.position.set(ex, coneH / 2, ez)
    cone.castShadow = false
    cone.receiveShadow = false
    group.add(cone)

    // Pin head: sphere on top
    const sphereR = 0.18
    const sphereGeo = new THREE.SphereGeometry(sphereR, 12, 12)
    const sphereMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 })
    const sphere = new THREE.Mesh(sphereGeo, sphereMat)
    sphere.position.set(ex, coneH + sphereR * 0.5, ez)
    sphere.castShadow = false
    sphere.receiveShadow = false
    group.add(sphere)

    // Coordinate label (canvas texture → sprite)
    const lat = anchor.origin.lat.toFixed(6)
    const lon = anchor.origin.lon.toFixed(6)
    const label = `${lat}, ${lon}`
    const sprite = this.createTextSprite(label, color)
    sprite.position.set(ex, coneH + sphereR * 2 + 0.25, ez)
    group.add(sprite)

    // Use tracked visibility state (map tab may already be active when marker is created)
    group.visible = this.anchorMarkerVisible
    this.anchorMarker = group
    this.scene.getScene().add(group)

    debug.log('Scheme', `Geo-anchor marker at editor (${anchor.editorOrigin.x}, ${anchor.editorOrigin.y}), GPS (${lat}, ${lon})`)
  }

  /**
   * Create a text sprite from a canvas.
   */
  private createTextSprite(text: string, tintColor: number): THREE.Sprite {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    const fontSize = 48
    const padding = 16

    ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`
    const metrics = ctx.measureText(text)
    const textW = metrics.width

    canvas.width = textW + padding * 2
    canvas.height = fontSize + padding * 2

    // Background pill
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
    const r = (fontSize + padding * 2) / 2
    ctx.beginPath()
    ctx.roundRect(0, 0, canvas.width, canvas.height, r)
    ctx.fill()

    // Text
    const hex = '#' + tintColor.toString(16).padStart(6, '0')
    ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = hex
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.fillText(text, canvas.width / 2, canvas.height / 2)

    const texture = new THREE.CanvasTexture(canvas)
    texture.minFilter = THREE.LinearFilter
    const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false })
    const sprite = new THREE.Sprite(mat)

    // Scale sprite to reasonable world size (1 world unit ≈ canvas height)
    const aspect = canvas.width / canvas.height
    const spriteH = 0.4
    sprite.scale.set(spriteH * aspect, spriteH, 1)

    return sprite
  }

  /**
   * Show or hide the geo-anchor marker independently of overall scheme visibility.
   * Used to only show the marker on the map layer tab.
   */
  setAnchorMarkerVisible(visible: boolean): void {
    this.anchorMarkerVisible = visible
    if (this.anchorMarker) {
      this.anchorMarker.visible = visible
    }
  }

  /**
   * Set visibility of the scheme layer
   */
  setVisible(visible: boolean): void {
    this.visible = visible
    if (this.mesh) {
      this.mesh.visible = visible
    }
    if (this.outlineMesh) {
      this.outlineMesh.visible = visible
    }
    // Anchor marker visibility is controlled separately via setAnchorMarkerVisible
    debug.log('Scheme', `Visibility set to: ${visible}`)
  }

  /**
   * Get current visibility state
   */
  isVisible(): boolean {
    return this.visible
  }

  /**
   * Check if scheme is loaded
   */
  isLoaded(): boolean {
    return this.mesh !== null
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    if (this.mesh) {
      this.scene.getScene().remove(this.mesh)
      this.mesh.geometry.dispose()
      if (this.mesh.material instanceof THREE.Material) {
        const material = this.mesh.material as THREE.MeshBasicMaterial
        material.map?.dispose()
        material.dispose()
      }
      this.mesh = null
    }
    if (this.outlineMesh) {
      this.scene.getScene().remove(this.outlineMesh)
      // Handle both single mesh and group
      if ((this.outlineMesh as unknown as THREE.Group).isGroup) {
        const group = this.outlineMesh as unknown as THREE.Group
        group.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose()
            if (child.material instanceof THREE.Material) {
              child.material.dispose()
            }
          }
        })
      } else {
        this.outlineMesh.geometry?.dispose()
        if (this.outlineMesh.material instanceof THREE.Material) {
          this.outlineMesh.material.dispose()
        }
      }
      this.outlineMesh = null
    }
    if (this.anchorMarker) {
      this.scene.getScene().remove(this.anchorMarker)
      this.anchorMarker.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          if (child.material instanceof THREE.Material) child.material.dispose()
        }
        if (child instanceof THREE.Sprite) {
          child.material.map?.dispose()
          child.material.dispose()
        }
      })
      this.anchorMarker = null
    }
    this.currentOutline = null
    debug.log('Scheme', 'Scheme renderer disposed')
  }
}
